import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'
import { baseUrl } from '@/utils/request'
import { languageApiLocale, readLanguage } from '@/utils/locale'
import { translateText } from '@/i18n'
import {
  authorizeHumanRequest,
  clearLoginSession,
  humanVerificationResult,
  inspectHumanVerificationResponse
} from '@/utils/humanVerification'

const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded'
const orbitRequest = axios.create({
  baseURL: baseUrl,
  timeout: 60000,
  withCredentials: true
})

let tokenWarningShown = false

function bearerToken() {
  return String(window.localStorage.getItem('token') || '')
    .replace(/^Bearer\s+/i, '')
    .trim()
}

function encodeForm(params = {}) {
  return Object.keys(params)
    .filter(key => params[key] !== undefined && params[key] !== null)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
    .join('&')
}

function responseHeader(headers, name) {
  if (!headers) return ''
  const target = String(name).toLowerCase()
  const key = Object.keys(headers).find(item => String(item).toLowerCase() === target)
  return key ? headers[key] : ''
}

function clearExpiredLogin() {
  clearLoginSession()
  if (!tokenWarningShown) {
    tokenWarningShown = true
    Message.warning(translateText('登录身份已过期，请重新登录！'))
  }
  if (!router.currentRoute || router.currentRoute.path !== '/login') {
    router.replace('/login').catch(error => {
      if (!error || error.name !== 'NavigationDuplicated') console.error(error)
    })
  }
}

function normalizeResponse(response) {
  const nextToken = responseHeader(response && response.headers, 'X-New-Token')
  if (nextToken) window.localStorage.setItem('token', nextToken)

  const body = response && response.data && typeof response.data === 'object'
    ? { ...response.data }
    : { message: response && response.data ? String(response.data) : 'Network error' }
  const status = Number(response && response.status)
  if (status === 401 || String(body.statusCode || '') === '40101') {
    body.success = false
    clearExpiredLogin()
  } else if (body.success && bearerToken()) {
    tokenWarningShown = false
  }
  if (!(status >= 200 && status < 300)) {
    body.success = false
    body.httpStatus = status
  }
  return body
}

async function requestOrbit(url, params = {}, method = 'POST') {
  try {
    const language = readLanguage()
    const requestConfig = {
      url,
      method,
      params: method === 'GET' ? params : undefined,
      data: method === 'POST' ? encodeForm(params) : undefined,
      headers: {
        Authorization: `Bearer ${bearerToken()}`,
        'Accept-Language': languageApiLocale(language),
        ...(method === 'POST' ? { 'Content-Type': FORM_CONTENT_TYPE } : {})
      }
    }
    await authorizeHumanRequest(requestConfig, baseUrl)
    const response = await orbitRequest.request(requestConfig)
    return normalizeResponse(response)
  } catch (error) {
    const response = error && error.response
    const verificationError = error && error.humanVerification
      ? error
      : inspectHumanVerificationResponse(
        error && error.config,
        response && response.status,
        response && response.data,
        baseUrl
      )
    const verificationResult = humanVerificationResult(verificationError)
    if (verificationResult) return verificationResult
    if (response) return normalizeResponse(response)
    throw error
  }
}

export const addOrbitRemoteStrategy = params => requestOrbit('/remote-strategy/add', params)
export const updateOrbitRemoteStrategy = params => requestOrbit('/remote-strategy/update', params)
export const startOrbitRemoteStrategy = params => requestOrbit('/remote-strategy/start', params)
export const stopOrbitRemoteStrategy = params => requestOrbit('/remote-strategy/stop', params)
export const deleteOrbitRemoteStrategy = params => requestOrbit('/remote-strategy/delete', params)
export const getOrbitRemoteStrategyDetail = params => requestOrbit('/remote-strategy/detail', params, 'GET')
export const getOrbitRemoteStrategyPositions = params => requestOrbit('/remote-strategy/currentPositions', params, 'GET')
export const getOrbitRemoteStrategyOrders = params => requestOrbit('/remote-strategy/currentOrders', params, 'GET')
